// HeaderWithBackButton.tsx
'use client';

import Link from 'next/link';
import paths from '@/paths';
import { ChevronLeft, CloseIcon } from '@/components/icons';
import { ButtonClassic } from '@/components/buttons/ButtonClassic';
import { useParams, usePathname, useRouter, useSearchParams } from 'next/navigation';
import { useEffect, useMemo, useState, Suspense } from 'react';
import { CurrencySelector } from '../CurrencySelector';
import { useHideOnScroll } from './hooks/useHideOnScroll';

// Separate the component that uses useSearchParams
const HeaderWithBackButtonContent = () => {
  const router = useRouter();
  const pathname = usePathname();
  const params = useParams();
  const searchParams = useSearchParams(); // 👈 This is what requires Suspense
  const hidden = useHideOnScroll();
  const tourId = params.tourId as string;
  const [step, setStep] = useState<1 | 2 | 3>(1);

  const backPath = useMemo(() => {
    if (tourId) {
      switch (step) {
        case 1:
          return '#';
        case 2:
          return paths.reservation.contact(tourId, searchParams.toString());
        case 3:
          return paths.reservation.paymentDetails(tourId, searchParams.toString());
        default:
          return '#';
      }
    } else {
      return '#';
    }
  }, [tourId, searchParams, step]);

  const exitPath = Number(tourId) ? paths.tourShow(Number(tourId)) : paths.home();
  const handleExit = () => router.push(exitPath);

  useEffect(() => {
    if (pathname.includes(paths.reservation.contact(tourId, '').replace('?', ''))) {
      setStep(1);
    } else if (pathname.includes(paths.reservation.paymentDetails(tourId, '').replace('?', ''))) {
      setStep(2);
    } else if (pathname.includes(paths.reservation.confirmation(tourId, '').replace('?', ''))) {
      setStep(3);
    } else {
      setStep(1);
    }
  }, [tourId, pathname]);

  return (
    <>
      {/** DESKTOP **/}
      <header className="hidden lg:block sticky w-full top-0 left-0 px-4 bg-header backdrop-blur-md border-b border-gray-800 z-[1100]">
        <div className="main-container relative mx-auto h-100 min-h-20 flex flex-wrap items-center justify-between">
          <div className="w-fit lg:w-fit flex justify-start z-[2]">
            <Link href={paths.home()}>
              <img src="/static/images/logo.png" alt="FaunaExplore logo" height={42} width={37} />
            </Link>
          </div>
          <div className="w-fit lg:w-fit min-h-20 flex items-center justify-end gap-2 sm:gap-3 z-[2]">
            <CurrencySelector />
            <ButtonClassic className="border border-gray-700" handleClick={handleExit}>
              {pathname.includes('draft') ? 'Save & Exit' : 'Exit'}
            </ButtonClassic>
          </div>
        </div>
      </header>

      {/** MOBILE **/}
      <header
        className={`block lg:hidden sticky w-full top-0 left-0 px-4 bg-header backdrop-blur-md border-b border-gray-800 z-[1100] transition-transform duration-300 ${hidden ? '-translate-y-full' : 'translate-y-0'}`}
      >
        <div className="main-container relative mx-auto h-100 min-h-20 flex flex-wrap items-center justify-between">
          <div className="w-fit lg:w-fit flex justify-start z-[2]">
            {step > 1 && (
              <Link href={backPath}>
                <ChevronLeft />
              </Link>
            )}
          </div>
          <div className="w-fit lg:w-fit min-h-20 flex items-center justify-end gap-2 z-[2]">
            <CurrencySelector />
            <Link href={exitPath} className="p-2">
              <CloseIcon />
            </Link>
          </div>
        </div>
      </header>
    </>
  );
};

// Wrap in Suspense before exporting
export const HeaderWithBackButton = () => {
  return (
    <Suspense
      fallback={
        <header className="sticky w-full top-0 left-0 px-4 bg-header backdrop-blur-md border-b border-gray-800 z-[1100]">
          <div className="main-container relative mx-auto h-100 min-h-20 flex flex-wrap items-center justify-between" />
        </header>
      }
    >
      <HeaderWithBackButtonContent />
    </Suspense>
  );
};
